<template>
  <div class="ev-page">
    <!-- ─── HEADER ─── -->
    <div class="ev-header">
      <div class="ev-header__info">
        <div class="ev-header__icon ev-header__icon--vendas">
          <i class="fa fa-basket-loaded"></i>
        </div>
        <div>
          <h1 class="ev-header__title">Expedição de Vendas</h1>
          <span class="ev-header__sub">Conferência de produtos por código de barras</span>
        </div>
      </div>
      
      <div class="ev-header__controls">
        <div class="ev-refresh-control mr-3 d-none d-lg-flex">
          <div class="ev-refresh-info">
            <i class="fa fa-refresh mr-1" :class="{'fa-spin': carregando}"></i>
            <span>Auto-atualização: <strong>{{ refreshInterval }}s</strong></span>
          </div>
          <b-form-input
            v-model="refreshInterval"
            type="range"
            min="5"
            max="60"
            step="5"
            class="ev-refresh-slider"
            :disabled="carregando"
          />
        </div>
        
        <b-button variant="primary" pill class="px-3 font-weight-bold shadow-sm" @click="carregarPedidos" :disabled="carregando">
          <i class="fa fa-refresh mr-1" :class="{'fa-spin': carregando}"></i> Atualizar
        </b-button>
      </div>
    </div>

    <!-- ─── CARDS DE RESUMO ─── -->
    <div class="ev-cards">
      <div class="ev-card ev-card--blue shadow-xs">
        <div class="ev-card__num">{{ pedidos.length }}</div>
        <div class="ev-card__label">Total de Pedidos</div>
      </div>
      <div class="ev-card ev-card--indigo shadow-xs">
        <div class="ev-card__num">{{ contarStatus('4') }}</div>
        <div class="ev-card__label">Aguardando</div>
      </div>
      <div class="ev-card ev-card--amber shadow-xs">
        <div class="ev-card__num">{{ contarStatus('5') }}</div>
        <div class="ev-card__label">Em Conferência</div>
      </div>
      <div class="ev-card ev-card--teal shadow-xs">
        <div class="ev-card__num font-weight-bold">{{ formatarQuantTotal(totalConferidoGeral) }}</div>
        <div class="ev-card__label">Total Conferido (kg)</div>
      </div>
    </div>

    <!-- ─── FILTROS ─── -->
    <div class="ev-filtros">
      <div class="ev-filtros__search shadow-xs">
        <span class="ev-filtros__icon"><i class="fa fa-search"></i></span>
        <input
          v-model="filtro"
          class="ev-filtros__input"
          placeholder="Filtrar por cliente, pedido ou vendedor..."
          @input="aplicarFiltros"
        />
      </div>
      <div class="ev-filtros__chips">
        <button
          class="ev-chip shadow-xs"
          :class="statusFiltro === null ? 'ev-chip--active' : ''"
          @click="setStatus(null)"
        >Todos ({{ pedidos.length }})</button>
        <button
          class="ev-chip ev-chip--indigo shadow-xs"
          :class="statusFiltro === '4' ? 'ev-chip--active' : ''"
          @click="setStatus('4')"
        >Aguardando ({{ contarStatus('4') }})</button>
        <button
          class="ev-chip ev-chip--amber shadow-xs"
          :class="statusFiltro === '5' ? 'ev-chip--active' : ''"
          @click="setStatus('5')"
        >Em Conferência ({{ contarStatus('5') }})</button>
      </div>
    </div>

    <!-- ─── TABELA ─── -->
    <div class="ev-table-card shadow-sm">
      <b-overlay :show="carregando" rounded="sm" opacity="0.6">
        <b-table
          :items="pedidosFiltrados"
          :fields="campos"
          hover
          responsive
          show-empty
          empty-text="Nenhum pedido elegível para expedição"
          class="ev-table mb-0"
          thead-class="ev-thead"
          @row-clicked="onRowClicked"
          :tbody-tr-class="rowClass"
        >
          <!-- Toggle Detalhes -->
          <template #cell(DETALHE)="row">
            <b-button variant="link" class="p-0 text-secondary" @click.stop="toggleDetalhes(row)">
              <i :class="row.detailsShowing ? 'fa fa-angle-up' : 'fa fa-angle-down'"></i>
            </b-button>
          </template>

          <!-- Pedido # -->
          <template #cell(SEQUENCIA)="{ item }">
            <span class="ev-seq">#{{ item.SEQUENCIA }}</span>
          </template>

          <!-- Data -->
          <template #cell(DT_SAIDA)="{ item }">
            <span class="ev-date">{{ formatarData(item.DT_SAIDA) }}</span>
          </template>

          <!-- Cliente -->
          <template #cell(NOME_CLIENTE)="{ item }">
            <div class="ev-cliente">
              <div class="ev-cliente__avatar">{{ inicialCliente(item.NOME_CLIENTE) }}</div>
              <div>
                <div class="ev-cliente__nome">{{ item.NOME_CLIENTE || '—' }}</div>
                <div class="ev-cliente__cod" v-if="item.NOME_VENDEDOR">{{ item.NOME_VENDEDOR }}</div>
              </div>
            </div>
          </template>

          <!-- Status -->
          <template #cell(SITUACAO)="{ item }">
            <span class="ev-status" :class="situacaoClass(item.SITUACAO)">
              <span class="ev-status__dot"></span>
              {{ situacaoTexto(item.SITUACAO) }}
            </span>
          </template>

          <!-- Qtd Pedida -->
          <template #cell(PESO_PEDIDO)="{ item }">
            <span class="ev-num ev-num--gray">{{ formatarQuant(item.PESO_PEDIDO) }}</span>
          </template>

          <!-- Qtd Conferida -->
          <template #cell(PESO_CONFERIDO)="{ item }">
            <span class="ev-num" :class="Number(item.PESO_CONFERIDO) > 0 ? 'ev-num--green' : 'ev-num--gray'">
              {{ formatarQuant(item.PESO_CONFERIDO) }}
            </span>
          </template>

          <!-- Progresso -->
          <template #cell(PROGRESSO)="{ item }">
            <div class="ev-prog-wrap">
              <div class="ev-prog-bar">
                <div
                  class="ev-prog-fill"
                  :style="{ width: calcProgresso(item) + '%' }"
                  :class="progressoFillClass(calcProgresso(item))"
                ></div>
              </div>
              <span class="ev-prog-pct">{{ calcProgresso(item) }}%</span>
            </div>
          </template>

          <!-- Ação -->
          <template #cell(ACOES)="{ item }">
            <div class="d-flex align-items-center gap-2">
              <button class="ev-btn-acao shadow-sm" :class="item.SITUACAO === '4' ? 'ev-btn-acao--green' : 'ev-btn-acao--amber'" @click.stop="abrirTerminal(item)">
                <i :class="item.SITUACAO === '4' ? 'fa fa-play' : 'fa fa-pencil'"></i>
                {{ item.SITUACAO === '4' ? 'Iniciar' : 'Continuar' }}
              </button>
              
              <button 
                class="ev-btn-acao ev-btn-acao--blue shadow-sm" 
                @click.stop="imprimirEspelho(item)" 
                title="Imprimir Espelho da Conferência"
                :disabled="Number(item.PESO_CONFERIDO) === 0"
              >
                <i class="fa fa-print"></i>
              </button>
            </div>
          </template>

          <!-- ROW DETAILS -->
          <template #row-details="row">
            <div class="p-3 bg-light border-top border-bottom">
              <div v-if="row.item.loadingItens" class="text-center p-3">
                <b-spinner small variant="primary" /> Carregando itens...
              </div>
              <div v-else>
                <div class="mb-3 d-flex justify-content-between align-items-center">
                  <h6 class="modal-section-title mb-0 font-weight-bold"><i class="fa fa-list mr-2 text-primary"></i>ITENS DO PEDIDO</h6>
                  <b-badge variant="soft-dark" pill>{{ (row.item.itens || []).length }} itens</b-badge>
                </div>
                
                <b-table
                  :items="row.item.itens"
                  :fields="camposItens"
                  small hover striped borderless
                  class="bg-white rounded shadow-xs mb-0"
                  thead-class="bg-light font-xs text-muted"
                >
                  <template #cell(QUANT_PEDIDO)="d">
                    <span class="font-weight-bold">{{ formatarQuant(d.item.QUANT_PEDIDO) }}</span>
                  </template>
                  <template #cell(QUANT_CONFERIDA)="d">
                    <span class="font-weight-bold text-success">{{ formatarQuant(d.item.QUANT_CONFERIDA) }}</span>
                  </template>
                  <template #cell(PERCENT)="d">
                    <small class="font-weight-bold" :class="calcPctItem(d.item) >= 100 ? 'text-success' : 'text-warning'">
                      {{ calcPctItem(d.item) }}%
                    </small>
                  </template>
                </b-table>
              </div>
            </div>
          </template>
        </b-table>

        <!-- Paginação -->
        <div class="ev-footer" v-if="pedidosFiltrados.length > porPagina">
          <span class="ev-footer__info">
            Exibindo {{ Math.min(paginaAtual * porPagina, pedidosFiltrados.length) }} de {{ pedidosFiltrados.length }}
          </span>
          <b-pagination
            v-model="paginaAtual"
            :total-rows="pedidosFiltrados.length"
            :per-page="porPagina"
            size="sm"
            align="right"
            class="mb-0"
          />
        </div>
      </b-overlay>
    </div>

    <!-- Modal Terminal -->
    <expedicao-vendas-modal
      ref="terminalModal"
      :pedido-id="selectedPedidoId"
      @concluido="carregarPedidos"
    />
  </div>
</template>

<script>
import ExpedicaoVendasModal from './ExpedicaoVendasModal.vue'
import VendaExpedicaoReport from '@/utils/reports/VendaExpedicaoReport'

export default {
  name: 'ExpedicaoVendasListagem',
  components: {
    ExpedicaoVendasModal
  },
  data () {
    return {
      carregando: false,
      pedidos: [],
      pedidosFiltrados: [],
      filtro: '',
      statusFiltro: null,
      paginaAtual: 1,
      porPagina: 20,
      selectedPedidoId: null,
      refreshInterval: 15,
      refreshTimer: null,
      campos: [
        { key: 'DETALHE', label: '', thStyle: { width: '40px' }, tdClass: 'text-center' },
        { key: 'SEQUENCIA', label: 'Pedido', sortable: true, thClass: 'ev-th' },
        { key: 'DT_SAIDA', label: 'Data', sortable: true, thClass: 'ev-th' },
        { key: 'NOME_CLIENTE', label: 'Cliente / Vendedor', sortable: true, thClass: 'ev-th' },
        { key: 'SITUACAO', label: 'Status', thClass: 'ev-th' },
        { key: 'PESO_PEDIDO', label: 'Peso Pedido', thClass: 'ev-th text-right', tdClass: 'ev-td text-right' },
        { key: 'PESO_CONFERIDO', label: 'Conferido', thClass: 'ev-th text-right', tdClass: 'ev-td text-right' },
        { key: 'PROGRESSO', label: 'Progresso', thClass: 'ev-th' },
        { key: 'ACOES', label: '', thClass: 'ev-th', tdClass: 'ev-td' }
      ],
      camposItens: [
        { key: 'ITEM', label: '#' },
        { key: 'COD_PRO', label: 'Cód' },
        { key: 'DESCRICAO', label: 'Produto' },
        { key: 'UNIDADE', label: 'Un' },
        { key: 'QUANT_PEDIDO', label: 'Pedida', class: 'text-right' },
        { key: 'QUANT_CONFERIDA', label: 'Conferida', class: 'text-right' },
        { key: 'PERCENT', label: '%', class: 'text-right' }
      ]
    }
  },
  computed: {
    totalConferidoGeral () {
      return this.pedidos.reduce((s, p) => s + Number(p.PESO_CONFERIDO || 0), 0)
    }
  },
  watch: {
    refreshInterval () {
      this.reiniciarTimer()
    }
  },
  created () {
    this.carregarPedidos()
    this.iniciarTimer()
  },
  beforeDestroy () {
    this.pararTimer()
  },
  methods: {
    async carregarPedidos () {
      if (this.carregando) return
      this.carregando = true
      try {
        const result = await this.$http.get('/api/protected/terminal-expedicao-vendas/pedidos-elegiveis')
        this.pedidos = result.data || []
        this.aplicarFiltros()
      } catch (err) {
        console.error('Erro ao carregar pedidos', err)
      } finally {
        this.carregando = false
      }
    },
    iniciarTimer () {
      this.pararTimer()
      this.refreshTimer = setInterval(() => {
        this.carregarPedidos()
      }, this.refreshInterval * 1000)
    },
    pararTimer () {
      if (this.refreshTimer) clearInterval(this.refreshTimer)
    },
    reiniciarTimer () {
      this.iniciarTimer()
    },
    aplicarFiltros () {
      let lista = [...this.pedidos]
      if (this.statusFiltro) lista = lista.filter(p => p.SITUACAO === this.statusFiltro)
      if (this.filtro.trim()) {
        const f = this.filtro.toLowerCase()
        lista = lista.filter(p =>
          String(p.SEQUENCIA).includes(f) ||
          (p.NOME_CLIENTE || '').toLowerCase().includes(f) ||
          (p.NOME_VENDEDOR || '').toLowerCase().includes(f)
        )
      }
      this.pedidosFiltrados = lista
    },
    onRowClicked (item) {
      this.selectedPedidoId = item.SEQUENCIA
    },
    rowClass (item) {
      if (!item) return ''
      return item.SEQUENCIA === this.selectedPedidoId ? 'ev-tr-selected' : 'ev-tr'
    },
    setStatus (valor) {
      this.statusFiltro = this.statusFiltro === valor ? null : valor
      this.aplicarFiltros()
    },
    contarStatus (valor) {
      return this.pedidos.filter(p => p.SITUACAO === valor).length
    },
    calcProgresso (pedido) {
      const p = Number(pedido.PESO_PEDIDO) || 0
      const e = Number(pedido.PESO_CONFERIDO) || 0
      if (p === 0) return 0
      return Math.min(100, Math.round((e / p) * 100))
    },
    calcPctItem (item) {
      const p = Number(item.QUANT_PEDIDO) || 0
      const e = Number(item.QUANT_CONFERIDA) || 0
      if (p === 0) return 0
      return Math.min(100, Math.round((e / p) * 100))
    },
    progressoFillClass (pct) {
      if (pct >= 100) return 'ev-prog-fill--green'
      if (pct >= 50) return 'ev-prog-fill--amber'
      return 'ev-prog-fill--blue'
    },
    situacaoTexto (s) {
      return { '4': 'Aguardando', '5': 'Conferindo' }[String(s || '').trim()] || s
    },
    situacaoClass (s) {
      return { '4': 'ev-status--indigo', '5': 'ev-status--amber' }[String(s || '').trim()] || ''
    },
    formatarQuant (v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    },
    formatarQuantTotal (v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },
    formatarData (v) {
      if (!v) return '—'
      return new Date(v).toLocaleDateString('pt-BR')
    },
    inicialCliente (nome) {
      if (!nome) return '?'
      return nome.trim().charAt(0).toUpperCase()
    },
    abrirTerminal (pedido) {
      this.selectedPedidoId = pedido.SEQUENCIA
      this.$nextTick(() => {
        this.$refs.terminalModal.abrir()
      })
    },
    async imprimirEspelho(pedido) {
      VendaExpedicaoReport.imprimir(pedido, this.$http, this.$store)
    },
    async toggleDetalhes (row) {
      if (row.detailsShowing) {
        row.toggleDetails()
        return
      }

      if (!row.item.itens) {
        this.$set(row.item, 'loadingItens', true)
        try {
          const response = await this.$http.get(`/api/protected/terminal-expedicao-vendas/itens-pedido/${row.item.SEQUENCIA}`)
          this.$set(row.item, 'itens', response.data.itens || [])
        } catch (err) {
          this.$bvToast.toast('Erro ao carregar itens', { variant: 'danger' })
        } finally {
          this.$set(row.item, 'loadingItens', false)
        }
      }
      row.toggleDetails()
    }
  }
}
</script>

<style scoped>
.ev-page {
  padding: 20px 24px;
  font-family: 'Segoe UI', 'Inter', sans-serif;
  background: #f4f6f9;
  min-height: calc(100vh - 60px);
}

/* ── HEADER ── */
.ev-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.08);
}
.ev-header__info { display: flex; align-items: center; gap: 14px; }
.ev-header__icon {
  width: 46px; height: 46px;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; color: #fff;
  box-shadow: 0 4px 14px rgba(79,70,229,0.35);
  flex-shrink: 0;
}
.ev-header__icon--vendas {
  background: linear-gradient(135deg, #ec4899, #8b5cf6);
  box-shadow: 0 4px 14px rgba(236,72,153,0.35);
}
.ev-header__title { font-size: 1.25rem; font-weight: 700; color: #1e293b; margin: 0; }
.ev-header__sub   { color: #64748b; font-size: 0.82rem; }

.ev-header__controls { display: flex; align-items: center; }
.ev-refresh-control { display: flex; flex-direction: column; min-width: 150px; }
.ev-refresh-info { font-size: 0.75rem; color: #64748b; margin-bottom: 4px; display: flex; justify-content: space-between; }
.ev-refresh-slider { height: 4px; padding: 0; }

/* ── CARDS ── */
.ev-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}
.ev-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  border-left: 4px solid transparent;
  transition: transform 0.15s;
}
.ev-card:hover { transform: translateY(-2px); }
.ev-card--blue   { border-color: #3b82f6; }
.ev-card--indigo { border-color: #6366f1; }
.ev-card--amber  { border-color: #f59e0b; }
.ev-card--teal   { border-color: #14b8a6; }
.ev-card__num  { font-size: 1.8rem; font-weight: 700; color: #1e293b; line-height: 1; }
.ev-card__label { color: #64748b; font-size: 0.78rem; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.04em; }

/* ── FILTROS ── */
.ev-filtros {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.ev-filtros__search { position: relative; flex: 1; min-width: 220px; }
.ev-filtros__icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }
.ev-filtros__input {
  width: 100%; border: 1px solid #e2e8f0; border-radius: 10px; padding: 8px 12px 8px 36px;
  font-size: 0.875rem; background: #fff; color: #1e293b; outline: none; transition: all 0.2s;
}
.ev-filtros__input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.12); }
.ev-filtros__chips { display: flex; gap: 8px; flex-wrap: wrap; }
.ev-chip {
  border: 1px solid #e2e8f0; background: #fff; border-radius: 20px; padding: 5px 14px;
  font-size: 0.8rem; cursor: pointer; color: #64748b; font-weight: 500; transition: all 0.15s; white-space: nowrap;
}
.ev-chip:hover { border-color: #c7d2fe; color: #4f46e5; }
.ev-chip--active { background: #4f46e5 !important; border-color: #4f46e5 !important; color: #fff !important; }

/* ── TABELA ── */
.ev-table-card { background: #fff; border-radius: 12px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); overflow: hidden; }
.ev-thead th {
  background: #f8fafc !important; color: #475569 !important; font-size: 0.73rem !important;
  text-transform: uppercase !important; letter-spacing: 0.06em !important; font-weight: 600 !important;
  border-bottom: 1px solid #e2e8f0 !important; padding: 10px 14px !important;
}
.ev-td { padding: 12px 14px !important; border-top: 1px solid #f1f5f9 !important; vertical-align: middle !important; }

.ev-tr-selected td { background-color: #eef2ff !important; border-left: 3px solid #4f46e5 !important; }

.ev-seq { font-family: 'Courier New', monospace; font-weight: 700; color: #4f46e5; }
.ev-cliente { display: flex; align-items: center; gap: 10px; }
.ev-cliente__avatar {
  width: 32px; height: 32px; background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; font-size: 0.85rem;
}
.ev-cliente__nome { font-weight: 600; color: #1e293b; font-size: 0.875rem; line-height: 1.2; }
.ev-cliente__cod { color: #94a3b8; font-size: 0.75rem; }

.ev-status { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 20px; font-size: 0.78rem; font-weight: 600; }
.ev-status__dot { width: 7px; height: 7px; border-radius: 50%; }
.ev-status--indigo { background: #eef2ff; color: #4338ca; }
.ev-status--indigo .ev-status__dot { background: #6366f1; }
.ev-status--amber { background: #fffbeb; color: #b45309; }
.ev-status--amber .ev-status__dot { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.25); animation: pulse 1.8s infinite; }

@keyframes pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.4); } 50% { box-shadow: 0 0 0 4px rgba(245,158,11,0); } }

.ev-num { font-variant-numeric: tabular-nums; font-size: 0.875rem; font-weight: 500; }
.ev-num--green { color: #059669; font-weight: 600; }

.ev-prog-wrap { display: flex; align-items: center; gap: 8px; min-width: 120px; }
.ev-prog-bar { flex: 1; height: 6px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.ev-prog-fill { height: 100%; border-radius: 4px; transition: width 0.5s ease; }
.ev-prog-fill--blue { background: linear-gradient(90deg, #3b82f6, #6366f1); }
.ev-prog-fill--amber { background: linear-gradient(90deg, #f59e0b, #fb923c); }
.ev-prog-fill--green { background: linear-gradient(90deg, #10b981, #059669); }
.ev-prog-pct { font-size: 0.75rem; color: #64748b; min-width: 30px; text-align: right; }

.ev-btn-acao {
  border: none; border-radius: 8px; padding: 6px 14px; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.ev-btn-acao--green { background: #dcfce7; color: #15803d; }
.ev-btn-acao--amber { background: #fef3c7; color: #b45309; }
.ev-btn-acao--blue { background: #e0f2fe; color: #0369a1; }
.ev-btn-acao--blue:hover { background: #0369a1; color: #fff; }
.ev-btn-acao:disabled { opacity: 0.5; cursor: not-allowed; }

.ev-footer { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; border-top: 1px solid #f1f5f9; }
.ev-footer__info { color: #94a3b8; font-size: 0.8rem; }

.bg-soft-dark { background-color: #334155; color: #fff; }
.font-xs { font-size: 0.75rem; }
.shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
</style>



// WEBPACK FOOTER //
// src/views/expedicao/vendas/ExpedicaoVendasListagem.vue